import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Star, MapPin, Clock, Flame, Users, ArrowRight, Mail, Check, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { pathFor } from "../lib/routing";
import { REVIEWS, WHATSAPP_NUMBER } from "../lib/reviews";
import { useI18n } from "../contexts/I18nContext";
import { submitReservation } from "../lib/booking";
import { toast } from "sonner";
import { FlagArgentina, FlagUSA } from "../components/PlaceFlags";

// Precio por persona según la sede. Si cambia, tocar también api/config.php
// (price_per_guest / miami_price_per_guest) y scripts/seo-prerender.js.
const PRICES = { argentina: 65, miami: 100 };
const PLACES = [
  { id: "argentina", Flag: FlagArgentina },
  { id: "miami", Flag: FlagUSA },
];
const MAX_GUESTS = 10;

const IMG = {
  hero: "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/7f7f3a4f-1933-402a-81fe-7a1d7c5879a5.jpeg",
  steps: [
    "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/63d6be53-7312-4840-9a52-438773afea60.jpeg",
    "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/9e76fcc5-b86e-4da0-8e5f-7bbde5dd62af.jpeg",
    "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/016ece4f-bd00-4f37-b10a-7a45657f9e1a.jpeg",
    "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/7f7f3a4f-1933-402a-81fe-7a1d7c5879a5.jpeg",
    "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/b084a4da-c67b-4a2b-a280-a7bc3a2f55cc.jpeg",
  ],
  gallery: "https://a0.muscache.com/im/pictures/Mt/MtTemplate-4390985/original/0c67aa44-fea5-4cef-bf23-2e25ce3e6129.jpeg",
  host: "https://a0.muscache.com/im/pictures/user/b4acdc56-9837-449c-88b6-44ceeeb7d126.jpg",
};

/* ================= HERO ================= */
function Hero() {
  const { t } = useI18n();
  const go = () => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
  const menu = () => document.getElementById("experience")?.scrollIntoView({ behavior: "smooth" });
  return (
    <section className="relative min-h-screen flex items-end overflow-hidden" aria-labelledby="hero-title" data-testid="hero-section">
      <img src={IMG.hero} alt="Asado argentino al fuego en Palermo Soho, Buenos Aires" className="absolute inset-0 w-full h-full object-cover animate-flicker" />
      <div className="absolute inset-0 bg-gradient-to-t from-charred via-charred/75 to-charred/30" />
      <div className="absolute inset-0 grain" />
      <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-12 pb-20 md:pb-28 pt-32">
        <span className="overline flex items-center gap-3" data-testid="hero-overline">
          <span className="divider-gold" /> {t.hero.overline}
        </span>
        <h1 id="hero-title" className="font-display font-light tracking-tight text-[#F5F3F0] mt-6 text-5xl md:text-7xl lg:text-[7.5rem] leading-[0.95]">
          {t.hero.title_1} <br />
          <em className="italic text-ember">{t.hero.title_2}</em>
        </h1>
        <p className="max-w-xl mt-8 text-base md:text-lg text-[#D1CEC9] leading-relaxed">
          {t.hero.subtitle}
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <button onClick={go} className="btn-primary" data-testid="hero-book-btn">
            <Flame size={14} /> {t.hero.cta}
          </button>
          <button onClick={menu} className="btn-ghost" data-testid="hero-menu-btn">
            {t.hero.secondary} →
          </button>
        </div>
        <div className="mt-14 flex items-center gap-4 text-xs uppercase tracking-[0.24em] text-[#A3A3A3]" data-testid="hero-rating">
          <div className="flex gap-[2px] text-ember">
            {[...Array(5)].map((_, i) => <Star key={i} size={12} fill="currentColor" strokeWidth={0} />)}
          </div>
          <span>{t.hero.rating_caption}</span>
        </div>
      </div>
    </section>
  );
}

/* ================= EXPERIENCE (5 steps) ================= */
function Experience() {
  const { t } = useI18n();
  return (
    <section id="experience" className="py-24 md:py-40 relative" aria-labelledby="experience-title" data-testid="experience-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.experience.overline}</span>
          <h2 id="experience-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">
            {t.experience.title}
          </h2>
          <p className="mt-6 text-[#A3A3A3] text-base md:text-lg leading-relaxed">{t.experience.subtitle}</p>
        </div>

        <div className="mt-20 md:mt-32 space-y-24 md:space-y-36">
          {t.experience.steps.map((step, i) => {
            const flip = i % 2 === 1;
            return (
              <div key={i}
                className={`grid md:grid-cols-12 gap-8 md:gap-16 items-center ${flip ? "md:[&>div:first-child]:order-2" : ""}`}
                data-testid={`experience-step-${i + 1}`}>
                <div className="md:col-span-7">
                  <div className="relative overflow-hidden group">
                    <img src={IMG.steps[i]} alt={step.title}
                      className="w-full h-[360px] md:h-[560px] object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                    <div className="absolute bottom-0 left-0 bg-charred border-t border-r border-ember/30 px-6 py-4">
                      <span className="font-display text-5xl md:text-6xl text-ember italic">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-5">
                  <span className="overline">Capítulo {String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-3xl md:text-5xl font-light tracking-tight mt-4 text-[#F5F3F0]">
                    {step.title}
                  </h3>
                  <p className="mt-6 text-[#A3A3A3] text-base md:text-lg leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ================= HOST ================= */
function Host() {
  const { t } = useI18n();
  return (
    <section id="host" className="py-24 md:py-40 bg-[#0a0a0a] border-y border-white/5 relative" data-testid="host-section">
      <div className="absolute inset-0 grain opacity-50" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20 items-center relative">
        <div className="md:col-span-5">
          <div className="relative">
            <div className="absolute -inset-3 border border-ember/30" />
            <img src={IMG.host} alt="Diego" className="relative w-full h-auto grayscale hover:grayscale-0 transition-all duration-700" />
          </div>
        </div>
        <div className="md:col-span-7">
          <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.host.overline}</span>
          <h2 className="font-display font-light text-5xl md:text-7xl tracking-tight mt-6 text-[#F5F3F0]">
            {t.host.title}
          </h2>
          <p className="mt-3 text-ember text-xs uppercase tracking-[0.24em]">{t.host.tagline}</p>
          <p className="mt-8 text-[#A3A3A3] text-base md:text-lg leading-relaxed max-w-2xl">{t.host.body}</p>
          <div className="mt-10 grid grid-cols-3 gap-8 border-t border-white/5 pt-8 max-w-lg">
            <div><p className="font-display text-3xl text-ember">20+</p><p className="overline mt-1">Años</p></div>
            <div><p className="font-display text-3xl text-ember">17k</p><p className="overline mt-1">Seguidores</p></div>
            <div><p className="font-display text-3xl text-ember">170+</p><p className="overline mt-1">Reseñas</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================= REVIEWS ================= */
function Reviews() {
  const { t, lang } = useI18n();
  const reviews = REVIEWS;
  const [i, setI] = useState(0);

  if (!reviews.length) return null;
  const next = () => setI((x) => (x + 1) % reviews.length);
  const prev = () => setI((x) => (x - 1 + reviews.length) % reviews.length);

  return (
    <section id="reviews" className="py-24 md:py-40" data-testid="reviews-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.reviews.overline}</span>
            <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">
              <span className="italic text-ember">170+</span> reseñas · <span className="italic text-ember">4.99</span> ★
            </h2>
          </div>
          <p className="text-[#A3A3A3] max-w-md">{t.reviews.subtitle}</p>
        </div>

        {/* Todas las reseñas se montan apiladas y se muestra la activa: así quedan
            en el DOM para los buscadores en vez de existir sólo la visible. */}
        <div className="relative border-y border-white/10 py-16 md:py-24 min-h-[320px]">
          <div className="grid grid-cols-1 grid-rows-1">
            {reviews.map((review, index) => (
              <blockquote key={review.author}
                aria-hidden={index !== i}
                className={`col-start-1 row-start-1 max-w-3xl mx-auto text-center transition-opacity duration-500 ${
                  index === i ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}>
                <div className="flex justify-center gap-1 text-ember mb-8">
                  {[...Array(5)].map((_, k) => <Star key={k} size={14} fill="currentColor" strokeWidth={0} />)}
                </div>
                <p className="font-display italic text-2xl md:text-3xl text-[#F5F3F0] leading-relaxed">
                  &ldquo;{lang === "en" ? review.content_en : review.content_es}&rdquo;
                </p>
                <footer className="mt-10">
                  <p className="text-[#F5F3F0] text-sm tracking-[0.2em] uppercase">{review.author}</p>
                  <p className="text-[#A3A3A3] text-xs mt-1">{review.location}</p>
                </footer>
              </blockquote>
            ))}
          </div>

          <button onClick={prev} data-testid="review-prev-btn"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 h-12 w-12 border border-white/10 hover:border-ember text-[#F5F3F0] hover:text-ember transition-colors flex items-center justify-center">
            <ChevronLeft size={18} />
          </button>
          <button onClick={next} data-testid="review-next-btn"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 h-12 w-12 border border-white/10 hover:border-ember text-[#F5F3F0] hover:text-ember transition-colors flex items-center justify-center">
            <ChevronRight size={18} />
          </button>
        </div>

        <p className="text-center text-xs text-[#A3A3A3] uppercase tracking-[0.24em] mt-6">
          {i + 1} / {reviews.length}
        </p>
      </div>
    </section>
  );
}

/* ================= LOCATION ================= */
function Location() {
  const { t, lang } = useI18n();
  return (
    <section id="location" className="py-24 md:py-40 bg-[#0a0a0a] border-y border-white/5" aria-labelledby="location-title" data-testid="location-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        <div>
          <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.location.overline}</span>
          <h2 id="location-title" className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">
            {t.location.title}
          </h2>
          <p className="mt-6 text-[#A3A3A3] text-base md:text-lg leading-relaxed max-w-lg">{t.location.body}</p>
          <div className="mt-8 flex items-center gap-3 text-[#F5F3F0]">
            <MapPin size={16} className="text-ember" />
            <span className="text-sm">{t.location.neighborhood}</span>
          </div>
          <div className="mt-3 flex items-center gap-3 text-[#F5F3F0]">
            <Clock size={16} className="text-ember" />
            <span className="text-sm">{t.location.hours}</span>
          </div>
          <div className="mt-3 flex items-center gap-3 text-[#F5F3F0]">
            <Users size={16} className="text-ember" />
            <span className="text-sm">{t.location.capacity} · {t.location.languages}</span>
          </div>
          <Link to={pathFor("palermo", lang)} className="btn-ghost mt-8 inline-flex" data-testid="location-palermo-link">
            {t.footer.palermo_link} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="relative">
          <img src={IMG.gallery} alt="Mesa de asado argentino y experiencia gastronómica en Palermo Soho" className="w-full h-[420px] md:h-[560px] object-cover" />
          <div className="absolute inset-0 border border-ember/20 m-3 pointer-events-none" />
        </div>
      </div>
    </section>
  );
}

/* ================= FAQ ================= */
function FAQ() {
  const { t } = useI18n();
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-40" data-testid="faq-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.faq.overline}</span>
        <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">{t.faq.title}</h2>
        <div className="mt-16 border-t border-white/10">
          {t.faq.items.map((item, i) => (
            <div key={i} className="border-b border-white/10">
              <button onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between py-6 md:py-8 text-left hover:text-ember transition-colors"
                data-testid={`faq-q-${i}`}>
                <span className="font-display text-xl md:text-2xl text-[#F5F3F0] pr-6">{item.q}</span>
                <span className={`text-ember text-xl transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              <div className={`overflow-hidden transition-all duration-500 ${open === i ? "max-h-96 pb-8" : "max-h-0"}`}>
                <p className="text-[#A3A3A3] text-base leading-relaxed max-w-2xl">{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================= BOOKING (WhatsApp) ================= */
const TIME_SLOTS = [
  { time: "13:00", key: "slot_lunch" },
  { time: "19:30", key: "slot_dinner" },
];

function Booking() {
  const { t, lang } = useI18n();
  const todayISO = new Date().toISOString().slice(0, 10);
  const [place, setPlace] = useState("argentina");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", guests: 1, date: "", time_slot: "", notes: "",
    website: "", // honeypot: invisible para una persona, irresistible para un bot
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const openedAt = useRef(Date.now());

  const guests = Math.min(MAX_GUESTS, Math.max(1, parseInt(form.guests, 10) || 1));
  const setGuests = (n) =>
    setForm((f) => ({ ...f, guests: Math.min(MAX_GUESTS, Math.max(1, n)) }));

  const pricePerGuest = PRICES[place];
  const current = t.booking.places[place];
  const total = (pricePerGuest * guests).toFixed(2);

  const slotLabel = (time) => {
    const slot = TIME_SLOTS.find((s) => s.time === time);
    return slot ? t.booking[slot.key] : time;
  };

  const prettyDate = () =>
    new Date(form.date + "T12:00:00").toLocaleDateString(
      lang === "en" ? "en-US" : "es-AR",
      { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    );

  // El email es opcional para WhatsApp (el pedido viaja en el chat) y
  // obligatorio para el envío por mail, que es a donde va la confirmación.
  const validate = ({ requireEmail }) => {
    if (!form.name.trim()) {
      toast.error(lang === "en" ? "Enter your name" : "Ingresá tu nombre");
      return false;
    }
    if (!form.date || !form.time_slot) {
      toast.error(lang === "en" ? "Pick a date and time" : "Elegí fecha y horario");
      return false;
    }
    if (requireEmail && !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error(t.booking.email_required);
      return false;
    }
    return true;
  };

  const submitWhatsApp = (e) => {
    e.preventDefault();
    if (!validate({ requireEmail: false })) return;

    const lines = [
      t.booking.wa_greeting,
      "",
      `${t.booking.place_wa}: ${current.wa}`,
      `${t.booking.name}: ${form.name}`,
      `${t.booking.guests}: ${guests}`,
      `${t.booking.date}: ${prettyDate()}`,
      `${t.booking.time}: ${form.time_slot} · ${slotLabel(form.time_slot)}`,
      form.phone.trim() ? `${t.booking.phone}: ${form.phone}` : null,
      form.email.trim() ? `${t.booking.email}: ${form.email}` : null,
      form.notes.trim() ? `${t.booking.notes}: ${form.notes}` : null,
      "",
      `${t.booking.total}: USD ${total}`,
    ].filter(Boolean);

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const submitEmail = async () => {
    if (sending || !validate({ requireEmail: true })) return;

    setSending(true);
    try {
      const result = await submitReservation(
        {
          place,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          guests,
          date: form.date,
          time_slot: form.time_slot,
          notes: form.notes.trim(),
          lang,
          website: form.website,
          elapsed_ms: Date.now() - openedAt.current,
        },
        { lang }
      );
      setSent({ ...result, email: form.email.trim() });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setSent(null);
    openedAt.current = Date.now();
    setPlace("argentina");
    setForm({ name: "", email: "", phone: "", guests: 1, date: "", time_slot: "", notes: "", website: "" });
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 text-[#F5F3F0] px-0 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-[#555] [color-scheme:dark]";
  const stepperBtnCls = "shrink-0 w-11 h-11 flex items-center justify-center border border-white/20 text-[#F5F3F0] text-2xl leading-none select-none transition-colors active:bg-ember/10 hover:border-ember hover:text-ember disabled:opacity-30 disabled:pointer-events-none";

  return (
    <section id="book" className="py-24 md:py-40 relative overflow-hidden" data-testid="booking-section">
      <div className="absolute inset-0 bg-gradient-to-b from-charred via-[#0d0a07] to-charred" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5 md:sticky md:top-32 h-fit">
          <span className="overline flex items-center gap-3"><span className="divider-gold" />{t.booking.overline}</span>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tight mt-6 text-[#F5F3F0]">
            {t.booking.title}
          </h2>
          <p className="mt-6 text-[#A3A3A3] text-lg">{t.booking.subtitle}</p>
          <div className="mt-10 border-t border-white/10 pt-8">
            <div className="flex justify-between text-sm text-[#A3A3A3] mb-3">
              <span>USD {pricePerGuest} × {guests}</span>
              <span className="text-[#F5F3F0]">USD {total}</span>
            </div>
            <div className="flex justify-between items-baseline border-t border-white/10 pt-4">
              <span className="overline">{t.booking.total}</span>
              <span className="font-display text-3xl text-ember">USD {total}</span>
            </div>
          </div>
          <div className="mt-6 border border-white/10 text-xs text-[#A3A3A3] leading-relaxed">
            <p className="p-4" data-testid="booking-whatsapp-note">{t.booking.whatsapp_note}</p>
            <p className="p-4 border-t border-white/10" data-testid="booking-email-note">{t.booking.email_note}</p>
          </div>
        </div>

        {sent ? (
          <div className="md:col-span-7" data-testid="booking-sent">
            <div className="border border-ember/40 bg-ember/[0.04] p-8 md:p-12">
              <span className="w-12 h-12 flex items-center justify-center border border-ember text-ember">
                <Check size={22} />
              </span>
              <h3 className="font-display font-light text-3xl md:text-4xl text-[#F5F3F0] mt-6">
                {t.booking.sent_title}
              </h3>

              <p className="mt-4 text-[#A3A3A3] leading-relaxed">
                {(sent.guestEmailed ? t.booking.sent_body : t.booking.sent_no_email)
                  .replace("__EMAIL__", sent.email)}
              </p>
              {sent.guestEmailed && (
                <p className="mt-3 text-sm text-[#777] leading-relaxed">{t.booking.sent_spam}</p>
              )}

              {sent.ref && (
                <div className="mt-8 border-t border-white/10 pt-6 flex items-baseline gap-4">
                  <span className="overline">{t.booking.sent_ref}</span>
                  <span className="font-display text-2xl text-ember tracking-wider">{sent.ref}</span>
                </div>
              )}

              <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer"
                  className="btn-primary justify-center" data-testid="booking-sent-whatsapp">
                  {t.booking.sent_whatsapp}
                </a>
                <button type="button" onClick={reset} className="btn-ghost justify-center" data-testid="booking-sent-again">
                  {t.booking.sent_again}
                </button>
              </div>
            </div>
          </div>
        ) : (
        <form onSubmit={submitWhatsApp} className="md:col-span-7 space-y-8" data-testid="booking-form">
          <div>
            <span className="overline block mb-3" id="booking-place-label">{t.booking.place_label}</span>
            <div className="grid grid-cols-2 gap-3" role="tablist" aria-labelledby="booking-place-label">
              {PLACES.map(({ id, Flag }) => {
                const active = place === id;
                return (
                  <button key={id} type="button" role="tab" aria-selected={active} onClick={() => setPlace(id)}
                    data-testid={`booking-tab-${id}`}
                    className={`flex items-center gap-3 md:gap-4 border px-3 md:px-5 py-4 text-left transition-colors ${active ? "border-ember bg-ember/10" : "border-white/10 hover:border-ember/50"}`}>
                    <Flag className={`w-9 md:w-11 shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-opacity ${active ? "" : "opacity-60"}`} />
                    <span className="min-w-0">
                      <span className={`block font-display text-lg md:text-xl leading-tight ${active ? "text-ember" : "text-[#F5F3F0]"}`}>{t.booking.places[id].tab}</span>
                      <span className="block text-[11px] text-[#A3A3A3] mt-1">{t.booking.places[id].place} · USD {PRICES[id]}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <label className="block">
              <span className="overline block mb-2">{t.booking.name}</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={lang === "en" ? "John Doe" : "Juan Pérez"} className={inputCls} data-testid="booking-name-input" />
            </label>
            <label className="block">
              <span className="overline block mb-2">{t.booking.email}</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className={inputCls} data-testid="booking-email-input" />
            </label>
            <label className="block">
              <span className="overline block mb-2">{t.booking.phone}</span>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={current.phone} className={inputCls} data-testid="booking-phone-input" />
            </label>
            <div className="block">
              <span className="overline block mb-2">{t.booking.guests}</span>
              <div className="flex items-center gap-3 border-b border-white/20">
                <button type="button" onClick={() => setGuests(guests - 1)} disabled={guests <= 1}
                  aria-label={lang === "en" ? "Fewer guests" : "Menos personas"}
                  className={stepperBtnCls} data-testid="booking-guests-minus">−</button>
                <input type="text" inputMode="numeric" pattern="[0-9]*" value={form.guests}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    if (v === "") return setForm({ ...form, guests: "" });
                    setForm({ ...form, guests: Math.min(MAX_GUESTS, Math.max(1, parseInt(v, 10))) });
                  }}
                  onBlur={() => setGuests(guests)}
                  className="flex-1 min-w-0 bg-transparent text-[#F5F3F0] text-center text-lg py-3 focus:outline-none"
                  data-testid="booking-guests-input" />
                <button type="button" onClick={() => setGuests(guests + 1)} disabled={guests >= MAX_GUESTS}
                  aria-label={lang === "en" ? "More guests" : "Más personas"}
                  className={stepperBtnCls} data-testid="booking-guests-plus">+</button>
              </div>
            </div>
          </div>

          <label className="block">
            <span className="overline block mb-2">{t.booking.date}</span>
            <input required type="date" min={todayISO} value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className={inputCls} data-testid="booking-date-input" />
          </label>

          <div>
            <span className="overline block mb-3">{t.booking.time}</span>
            <div className="grid md:grid-cols-2 gap-3">
              {TIME_SLOTS.map((s) => {
                const active = form.time_slot === s.time;
                return (
                  <button key={s.time} type="button"
                    onClick={() => setForm({ ...form, time_slot: s.time })}
                    data-testid={`slot-btn-${s.time}`}
                    className={`border px-5 py-4 text-left transition-colors
                      ${active ? "border-ember bg-ember/10" : "border-white/10 hover:border-ember/50"}`}>
                    <span className={`font-display text-xl ${active ? "text-ember" : "text-[#F5F3F0]"}`}>{s.time}</span>
                    <span className="block text-[11px] text-[#A3A3A3] mt-1">{t.booking[s.key]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <span className="overline block mb-2">{t.booking.notes}</span>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={lang === "en" ? "Allergies, preferences..." : "Alergias, preferencias..."} className={inputCls} data-testid="booking-notes-input" />
          </label>

          {/* Trampa para bots: fuera de la pantalla, sin tab y oculta a lectores. */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            className="absolute left-[-9999px] top-0 w-px h-px opacity-0 pointer-events-none" />

          <div className="flex flex-col sm:flex-row gap-4 sm:items-center pt-2">
            <button type="submit" disabled={sending}
              className="btn-primary justify-center" data-testid="booking-submit-btn">
              <Flame size={14} /> {t.booking.submit}
            </button>
            <button type="button" onClick={submitEmail} disabled={sending}
              className="btn-secondary justify-center" data-testid="booking-email-btn">
              {sending
                ? <><Loader2 size={14} className="animate-spin" /> {t.booking.sending}</>
                : <><Mail size={14} /> {t.booking.submit_email}</>}
            </button>
          </div>
        </form>
        )}
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <>
      <Hero />
      <Experience />
      <Host />
      <Reviews />
      <Location />
      <FAQ />
      <Booking />
    </>
  );
}
